import { useState } from 'react';
import { isEmpty } from 'lodash';
import { Icon, Button } from '@repo/ui';
import { AppealItem, MatterInvoiceSummaryFile, Timekeeper } from '../../types';
import { WriteOffStatus } from '@repo/types';
import { AttorneyResponseItem } from '../common/AttorneyResponseItem';
import { ClickableFileButton } from '../common/ClickableFileButton';

interface AppealAttorneyResponsesProps {
  appeal: AppealItem;
  appealsSupportFiles: MatterInvoiceSummaryFile[];
  writeOffStatus?: WriteOffStatus;
}

export const AppealAttorneyResponses = ({
  appeal,
  appealsSupportFiles,
  writeOffStatus,
}: AppealAttorneyResponsesProps) => {
  const [visibleResponsesCount, setVisibleResponsesCount] = useState(2);

  return (
    <div className="space-y-6">
      {/* Header & Appeals Support */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            ATTORNEY RESPONSE DETAILS
          </h4>
        </div>

        {/* Appeals Support Files */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500">APPEALS SUPPORT</span>
            <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
              {appealsSupportFiles.length}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {appealsSupportFiles.length > 0 ? (
              appealsSupportFiles.map(file => <ClickableFileButton key={file.fileId} file={file} />)
            ) : (
              <span className="text-sm text-gray-400 italic">No documents available</span>
            )}
          </div>
        </div>
      </div>

      {/* Attorney Responses List */}
      <div className="space-y-4">
        {isEmpty(appeal.timekeepers) === false ? (
          <>
            {(appeal.timekeepers || []).slice(0, visibleResponsesCount).map((tk: Timekeeper) => {
              const response = appeal.attorneyResponses?.find((r: any) => r.id === tk.id);
              if (!response) return null;
              return (
                <div
                  key={tk.id || tk.name} // Using ID or name as key
                  className="bg-white dark:bg-gray-800/30 rounded-lg overflow-hidden"
                >
                  <div className="p-0">
                    {/* Wrapper to override any default card padding if needed */}
                    <AttorneyResponseItem
                      appealAction={response?.appealAction}
                      lineItems={response?.lineItems}
                      attachments={response?.attachments}
                      submittedByRole={response?.submittedByRole}
                      submittedBy={response?.createdByName || response?.createdBy}
                      timekeeperName={tk.name}
                      submittedAt={response?.createdAt || new Date().toISOString()}
                      isSubmitted={!!response?.createdAt}
                      writeOffStatus={writeOffStatus}
                    />
                  </div>
                </div>
              );
            })}

            {/* Show More / Show Less Buttons */}
            {(appeal.timekeepers?.length || 0) > 2 && (
              <div className="flex justify-center pt-2">
                {visibleResponsesCount < (appeal.timekeepers?.length || 0) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleResponsesCount(prev => prev + 2)}
                    rightIcon={<Icon name="HiChevronDown" />}
                    className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  >
                    Show {Math.min(2, (appeal.timekeepers?.length || 0) - visibleResponsesCount)}{' '}
                    More Responses
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleResponsesCount(2)}
                    rightIcon={<Icon name="HiChevronUp" />}
                    className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  >
                    Show Less
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">No attorney responses found.</p>
          </div>
        )}
      </div>
    </div>
  );
};
